export const GROUP_TRENDS_SELECTION_STORAGE_KEY =
  'nwm.groupTrendsSelection';

export const persistGroupTrendsSelection = (selection = []) => {
  try {
    sessionStorage.setItem(
      GROUP_TRENDS_SELECTION_STORAGE_KEY,
      JSON.stringify(selection || []),
    );
  } catch (error) {
    // quota / private mode — Redux persist is the fallback
  }
};

export const readPersistedGroupTrendsSelection = () => {
  try {
    const raw = sessionStorage.getItem(GROUP_TRENDS_SELECTION_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

export const clearPersistedGroupTrendsSelection = () => {
  try {
    sessionStorage.removeItem(GROUP_TRENDS_SELECTION_STORAGE_KEY);
  } catch (error) {
    // ignore
  }
};

const profileName = (profile, language) =>
  profile?.[`name_${language}`] ||
  profile?.name_es ||
  profile?.name_en ||
  profile?.name_pt ||
  '';

const toNumericId = (id) => {
  if (id == null || id === '') return null;
  const numericId = Number(id);
  return Number.isNaN(numericId) ? null : numericId;
};

const isSameToolboxPerson = (left, right) => {
  const leftId = toNumericId(left?.id);
  const rightId = toNumericId(right?.id);
  if (leftId != null && rightId != null && leftId === rightId) return true;
  const leftResultId = toNumericId(left?.id_results ?? left?.id_result);
  const rightResultId = toNumericId(right?.id_results ?? right?.id_result);
  return (
    leftResultId != null &&
    rightResultId != null &&
    leftResultId === rightResultId
  );
};

export const uniqueToolboxPersons = (persons = []) => {
  const seen = new Set();
  return persons.filter((person) => {
    const id = toNumericId(person?.id ?? person?.id_invitation);
    const resultId = toNumericId(person?.id_results ?? person?.id_result);
    const keys = [
      id != null ? `i:${id}` : null,
      resultId != null ? `r:${resultId}` : null,
    ].filter(Boolean);
    if (!keys.length) return true;
    if (keys.some((key) => seen.has(key))) return false;
    keys.forEach((key) => seen.add(key));
    return true;
  });
};

export const hydratePersistedPersonSelection = (selection = [], language) => {
  const selectedPersons = uniqueToolboxPersons(
    selection
      .filter((item) => item?.profile?.id !== 27)
      .map((item) => ({
        id: toNumericId(item.id_invitation) ?? item.id_invitation,
        title: item.full_name,
        subtitle: {
          description: profileName(item.profile, language),
          code: item.profile?.id,
        },
        caption: {
          description: item?.campaign?.name || '',
          code: item?.campaign?.id,
        },
        id_results: toNumericId(item?.id_result) ?? item?.id_result,
        id_invitation: item?.id_invitation,
      })),
  );

  return {
    selectedPersons,
    selectedPersonIds: new Set(selectedPersons.map((person) => person.id)),
  };
};

// One selected identity -> at most one loaded row. Filtering the whole page
// by id OR id_results selected every duplicate invitation/result row, so a
// single Compatibilidad handoff checked several people.
export const remapSelectionOntoLoadedPersons = (
  loadedPersons = [],
  selectedPersons = [],
  language,
) => {
  const uniqueSelected = uniqueToolboxPersons(selectedPersons);
  const usedLoadedIds = new Set();

  const mapped = uniqueSelected.map((selected) => {
    const match = loadedPersons.find((person) => {
      const personId = toNumericId(person.id);
      if (personId != null && usedLoadedIds.has(personId)) return false;
      return isSameToolboxPerson(
        { id: person.id, id_results: person.result?.id ?? person.id_results },
        selected,
      );
    });
    if (!match) return selected;
    const personId = toNumericId(match.id);
    if (personId != null) usedLoadedIds.add(personId);
    return match.result
      ? mapResultsPersonToToolboxItem(match, language)
      : {
          ...selected,
          id: toNumericId(match.id) ?? match.id,
          id_results:
            toNumericId(match.result?.id ?? match.id_results) ??
            selected.id_results,
          id_invitation: match.id ?? selected.id_invitation,
        };
  });

  const uniqueMapped = uniqueToolboxPersons(mapped);
  return {
    selectedPersons: uniqueMapped,
    selectedPersonIds: new Set(uniqueMapped.map((person) => person.id)),
  };
};

// Compatibilidad used to do history.push + window.location.reload(). The
// reload unmounts this screen while the URL is still
// group-trends?selected-items=true. Clearing Redux selection here lets
// redux-persist write [] before the new page hydrates.
export const shouldPreservePersistedSelection = (location = {}) => {
  const pathname = location.pathname || '';
  if (pathname === '/main/persons' || pathname === '/') return true;
  if (pathname !== '/main/persons/group-trends') return false;
  return new URLSearchParams(location.search || '').has('selected-items');
};

export const resolveHandoffSelection = (reduxSelection = []) => {
  const fromStorage = readPersistedGroupTrendsSelection();
  return fromStorage.length ? fromStorage : reduxSelection || [];
};

export const mapResultsPersonToToolboxItem = (person, language) => ({
  id: toNumericId(person?.id) ?? person?.id,
  title: `${person.user_player?.user?.first_name || ''} ${
    person?.user_player?.user?.last_name || ''
  }`.trim(),
  subtitle: {
    description:
      person?.result?.nwm_result_distance_match?.profile?.[
        `name_${language}`
      ] || '',
    code: person?.result?.nwm_result_distance_match?.profile?.id,
  },
  caption: {
    description: person?.campaign?.name || '',
    code: person?.campaign?.id,
  },
  id_results: person?.result?.id,
  id_invitation: person.id,
});

export const mergeSelectedPersonsIntoItems = (
  pageItems = [],
  selectedPersons = [],
) => {
  const uniqueSelected = uniqueToolboxPersons(selectedPersons);
  const selectedMissing = uniqueSelected.filter(
    (person) =>
      person?.id != null &&
      !pageItems.some((item) => isSameToolboxPerson(item, person)),
  );
  return uniqueToolboxPersons([...selectedMissing, ...pageItems]);
};
